import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog,
    handleClose,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import {
    Cohort,
    CohortEditResource,
    CohortViewResource,
    Permission
} from "generated/openApi";
import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { CohortPageHeader } from "./CohortPageHeader";
import { CohortPageBody } from "./CohortPageBody";

const routeName = "cohort";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

class OptionalParams {
    closeOnSave?: boolean = false;
}

export const cohortPageRoute: PageRoute<Params, OptionalParams> = {
    handle: "Cohort",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
    getOptionalParams: (params) => buildOptionalParams(params),
};

export type CohortContextValue = {
    onCreate?: (id: number) => void;
};

export const CohortContext = createContext<CohortContextValue>({} as CohortContextValue);

export const CohortPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
    const [cohort, setCohort] = useState<Cohort>({} as Cohort);
    const [initCohort, setInitCohort] = useState<Cohort>(cohort);
    const [viewResource, setViewResource] = useState<CohortViewResource>({});
    const [editResource, setEditResource] = useState<CohortEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [cohort] });
    const { validationErrors, setValidationErrors } = useValidationErrors();
    const [closeOnSave] = useState<boolean>(optionalParams.closeOnSave || false);

    useEffect(() => {
        fetch(params.id);

        ApiService.cohorts.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.cohorts.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${cohortPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        if (id > 0) {
            ApiService.cohorts.get(id).then((response) => {
                setCohort(response);
                setInitCohort(response);
                resetUnsavedChanges([response]);
            });
        } else {
            // For new
            setCohort({} as Cohort);
            setInitCohort({} as Cohort);
            resetUnsavedChanges([{} as Cohort]);
        }
    };

    const pageHeader = (
        <CohortPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={cohortPageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            cohort={cohort}
            setCohort={setCohort}
            initCohort={initCohort}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
            closeOnSave={closeOnSave}
        />
    );

    const pageBody = (
        <CohortPageBody
            cohort={cohort}
            setCohort={setCohort}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
        />
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};
